<template>
  <div
    :class="`exercise-tile ${outlineClass}`"
    :data-id="calendarId"
    v-if="toShow"
  >
    <div class="exercise-tile--top">
      <div
        v-if="type == 'EXERCISE' || type == 'ACTIVITY' || noExerciseThumbnails"
        class="exercise-tile__thumbnail--wrap"
        :class="
          (additionalMedia && additionalMedia.processing) ||
          (!additionalMedia && media && media.processing)
            ? 'processing'
            : ''
        "
      >
        <template
          v-if="
            additionalMedia &&
              (additionalMedia.thumbnailUrl || additionalMedia.processing)
          "
        >
          <img
            v-if="
              additionalMedia &&
                additionalMedia.thumbnailUrl &&
                !additionalMedia.processing
            "
            class="exercise-tile__thumbnail"
            :src="additionalMedia.thumbnailUrl"
          />
          <v-progress-circular
            v-if="
              additionalMedia &&
                additionalMedia.processing &&
                !additionalMedia.thumbnailUrl
            "
            class="exercise-tile__thumbnail--loader"
            size="32"
            indeterminate
            color="bhBlue3"
            :width="3"
          ></v-progress-circular>
        </template>
        <template v-else-if="media && (media.thumbnailUrl || media.processing)">
          <img
            v-if="media && media.thumbnailUrl && !media.processing"
            class="exercise-tile__thumbnail"
            :src="media.thumbnailUrl"
          />
          <v-progress-circular
            v-if="media && media.processing && !media.thumbnailUrl"
            class="exercise-tile__thumbnail--loader"
            size="32"
            indeterminate
            color="bhBlue3"
            :width="3"
          ></v-progress-circular>
        </template>
        <img
          v-else-if="mobileStaticMedia && mobileStaticMedia.mediaUrl"
          class="exercise-tile__thumbnail"
          :src="mobileStaticMedia.mediaUrl"
        />
        <img
          v-else
          class="exercise-tile__thumbnail"
          :src="placeholder(media ? media.fileTypeId : null)"
        />
        <div
          class="exercise-tile__thumbnail--icon"
          :style="{ background: typeIcon.color }"
        >
          <v-icon size="16">{{ typeIcon.icon }}</v-icon>
          <span v-if="time">{{ time == 'EVENING' ? 'PM' : 'AM' }}</span>
        </div>
      </div>
      <div v-else class="exercise-tile__thumbnail--wrap">
        <img class="exercise-tile__thumbnail" :src="placeholder(18)" />
        <div
          class="exercise-tile__thumbnail--icon"
          :style="{ background: typeIcon.color }"
        >
          <v-icon size="16">{{ typeIcon.icon }}</v-icon>
          <span v-if="type === 'ACTIVITY'">{{
            activity.time == 'EVENING' ? 'PM' : 'AM'
          }}</span>
        </div>
      </div>
      <div v-if="exercise" class="exercise-tile__text">
        <h5>{{ exercise.title }}</h5>
        <v-tooltip v-if="description" bottom open-delay="200">
          <template v-slot:activator="{ on, attrs }">
            <p v-bind="attrs" v-on="on">{{ description }}</p>
          </template>
          <p>{{ description }}</p>
        </v-tooltip>
      </div>
      <div v-else-if="activity" class="exercise-tile__text">
        <h5>{{ activity.title }}</h5>
        <v-tooltip
          v-if="description"
          bottom
          open-delay="200"
          @mouseover.native.stop
        >
          <template v-slot:activator="{ on, attrs }" @mouseover.native.stop>
            <p v-bind="attrs" v-on="on">{{ description }}</p>
          </template>
          <span>{{ description }}</span>
        </v-tooltip>
      </div>
      <div class="exercise-tile__quick-edit" v-if="exercise">
        <div
          class="exercise-tile__quick-edit--input"
          v-for="key in exerciseAttributes"
          :key="key.id"
        >
          <div
            v-if="
              exercise.progress &&
                exercise.progress.length > 0 &&
                exercise.progress[0][key.id]
            "
          >
            <strong>{{ exercise.progress[0][key.id] }}</strong>
            <v-tooltip bottom v-if="key.tooltip">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">{{ key.label }}</span>
              </template>
              <span>{{ key.tooltip }}</span>
            </v-tooltip>
            <span v-else>{{ key.label }}</span>
          </div>
        </div>
      </div>
      <div class="exercise-tile__quick-edit activity" v-if="activity"></div>
    </div>
    <div class="exercise-tile--bottom">
      <div class="exercise-tile__report">
        <div>
          <rating-stars
            :max="5"
            v-if="exercise.rating || activity.rating"
            :rating="exercise.rating || activity.rating"
          />
          <v-tooltip
            open-delay="200"
            bottom
            v-if="exercise.ratingComment || activity.ratingComment"
          >
            <template v-slot:activator="{ on, attrs }">
              <p
                class="exercise-tile__report-rating-comment mt-2"
                v-bind="attrs"
                v-on="on"
              >
                {{ exercise.ratingComment || activity.ratingComment }}
              </p>
            </template>
            <span class="participant-list-item__notes-tooltip">{{
              exercise.ratingComment || activity.ratingComment
            }}</span>
          </v-tooltip>
        </div>
        <v-spacer />
        <div style="width: 120px;">
          <div v-for="key in exerciseAttributes" :key="key.id">
            <horizontal-value-bar
              v-if="computedProgress[key.id]"
              small
              :value="computedProgress[key.id].sum"
              :total="computedProgress[key.id].target"
              :type="key.id"
            />
          </div>
        </div>
        <p class="activity-skipped" v-if="activity && activity.skipped">
          {{
            tenantTexts.participantRoutinesProgress.sectionDailyPerformance
              .activitySkipped
          }}
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import RatingStars from '@/components/exercises/RatingStars.vue'
import HorizontalValueBar from '@/components/exercises/HorizontalValueBar.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'

export default {
  name: 'ExerciseReportTile',
  components: {
    RatingStars,
    HorizontalValueBar,
  },
  mixins: [TenantTextsMixin],
  props: {
    id: {
      type: Number,
      default: 0,
    },
    calendarId: {
      type: [Number, String],
      default: 0,
    },
    type: {
      type: String,
      default: 'EXERCISE',
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    time: {
      type: [Boolean, String],
      default: false,
    },
    exercise: {
      type: [Boolean, Object],
      default: false,
    },
    activity: {
      type: [Boolean, Object],
      default: false,
    },
    mode: {
      type: String,
      default: 'all',
    },
    showOutline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activityTime: 'none',
      menuHandler: false,
      exerciseResource: null,
      exerciseAttributes: [
        { id: 'repetitions', label: 'Reps' },
        { id: 'series', label: 'Sets' },
        { id: 'weight', label: 'lbs' },
        { id: 'time', label: 'minutes' },
        { id: 'distance', label: 'Miles' },
        {
          id: 'restTimeBetweenSeries',
          label: 'RTBS',
          tooltip: 'Rest time between sets',
        },
      ],
    }
  },
  watch: {},
  created() {
    if (this.activity?.time) this.activityTime = this.activity.time
  },
  computed: {
    ...mapState('dictionariesStates', ['dictionaryFileType']),
    outlineClass() {
      if (!this.showOutline) return ''

      const isSkipped = this.activity?.skipped || this.exercise?.skipped
      const isCompleted = this.activity?.completed || this.exercise?.completed
      const isSuperCompleted =
        this.activity?.superCompleted || this.exercise?.superCompleted
      const isPartially = this.activity?.partially || this.exercise?.partially

      if (isSuperCompleted) return 'exercise-tile--outline-purple'
      else if (isCompleted) return 'exercise-tile--outline-blue'
      else if (isPartially) return 'exercise-tile--outline-light-blue'

      return isSkipped
        ? 'exercise-tile--outline-red'
        : 'exercise-tile--outline-gray'
    },
    toShow() {
      if (this.mode == 'all') return true
      else if (this.mode == 'exercises' && this.type == 'EXERCISE') return true
      else if (
        this.mode == 'activitiesAM' &&
        this.type == 'ACTIVITY' &&
        this.activity?.time != 'EVENING'
      )
        return true
      else if (this.mode == 'activitiesPM' && this.activity?.time == 'EVENING')
        return true
      else if (
        this.mode == 'skipped' &&
        (this.exercise?.skipped || this.activity?.skipped)
      )
        return true
      else return false
    },
    typeIcon() {
      let typeObj = {
        color: '',
        icon: '',
      }
      switch (this.type) {
        case 'SERIES':
          typeObj.color = '#FAC032'
          typeObj.icon = 'bhi-list'
          break
        case 'ACTIVITY':
          typeObj.color = '#ED8938'
          typeObj.icon = 'bhi-activity'
          break
        default:
          typeObj.color = '#945EFF'
          typeObj.icon = 'bhi-exercise'
          break
      }
      return typeObj
    },
    noExerciseThumbnails() {
      if (
        this.type == 'SERIES' &&
        this.multiThumbnailComputed.findIndex(item => item.thumbnailUrl) == -1
      )
        return true
      else return false
    },
    computedProgress() {
      let ret = {}
      let series = this.exercise?.series || 1
      let repetitions = this.exercise?.repetitions || 1
      if (this.exercise?.progress) {
        this.exerciseAttributes.forEach(attr => {
          if (attr.id != 'series')
            if (Object.hasOwn(this.exercise, attr.id)) {
              if (attr.id == 'weight')
                ret[attr.id] = {
                  sum: 0,
                  target: this.exercise[attr.id] * series * repetitions,
                }
              else
                ret[attr.id] = {
                  sum: 0,
                  target: this.exercise[attr.id] * series,
                }
            }
        })
        this.exercise.progress.forEach(item => {
          Object.keys(item).forEach(key => {
            if (Object.hasOwn(ret, key)) {
              if (key == 'weight') ret[key].sum += item[key] * repetitions
              else ret[key].sum += item[key]
            }
          })
        })
      }
      return ret
    },
    media() {
      return this.exercise?.media || this.activity?.media || null
    },
    additionalMedia() {
      return (
        this.exercise?.additionalMedia || this.activity?.additionalMedia || null
      )
    },
    mobileStaticMedia() {
      return (
        this.exercise?.mobileStaticMedia ||
        this.activity?.mobileStaticMedia ||
        null
      )
    },
  },
  methods: {
    placeholder(fileTypeId) {
      let image = 'placeholder_other.png'
      switch (fileTypeId) {
        case 17:
          image = 'placeholder_other.png'
          break
        case 18:
          image = 'placeholder_image.png'
          break
        case 19:
          image = 'placeholder_video.png'
          break
        case 20:
          image = 'placeholder_pdf.png'
          break
        case 21:
          image = 'placeholder_doc.png'
          break
        case 22:
          image = 'placeholder_presentation.png'
          break
        case 23:
          image = 'placeholder_sheet.png'
          break
        default:
          break
      }
      return require('@/assets/' + image)
    },
  },
}
</script>

<style lang="scss" scoped>
.exercise-tile {
  width: 280px;
  min-height: 74px;
  border-radius: 8px;
  border: 1px solid $bh-white;
  padding: 5px;
  background: $bh-white;
  -webkit-transition-duration: 150ms;
  -moz-transition-duration: 150ms;
  -ms-transition-duration: 150ms;
  -o-transition-duration: 150ms;
  transition-duration: 150ms;
  display: flex;
  align-items: flex-start;
  margin: $gutter/2;
  flex-wrap: nowrap;
  position: relative;
  user-select: none;
  flex-wrap: wrap;
  align-content: flex-start;
  overflow: hidden;

  &--outline {
    &-red {
      border: 1px solid $bh-red;
    }
    &-blue {
      border: 1px solid $bh-blue;
    }
    &-gray {
      border: 1px solid $bh-gray--3;
    }
    &-purple {
      border: 1px solid --main-color;
    }
    &-lightblue {
      border: 1px solid $bh-blue--3;
    }
  }

  &--top {
    padding-left: 68px;
    display: flex;
    flex-wrap: wrap;
    position: relative;
    min-height: 80px;
    width: 100%;
  }
  &--bottom {
    width: 100%;
  }
  &.selected {
    border-color: $bh-blue--2;
    cursor: grab;
  }
  &:hover {
    .exercise-tile__more {
      opacity: 1;
    }
    .exercise-tile__collection {
      opacity: 0;
    }
  }
  &.bordered {
    background: transparent;
    border-color: $bh-blue;
  }
  &.create {
    padding-left: $gutter;
    .v-btn {
      &:nth-child(n + 2) {
        margin-left: 10px;
      }
    }
  }
  &__thumbnail {
    width: 64px;
    height: 64px;
    object-fit: cover;
    display: block;
    &--wrap {
      overflow: hidden;
      position: absolute;
      top: 4px;
      left: 4px;
      width: 64px;
      height: 64px;
      flex-shrink: 0;
      text-align: center;
      border-radius: 8px;
      &.processing {
        background: $bh-gray--6;
      }
    }
    &--loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate3d(-50%, -50%, 0px);
    }
    &--preview {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      z-index: 1;
      transition: 0.2s linear !important;
      .v-progress-linear__background,
      .v-progress-linear__buffer,
      .v-progress-linear__determinate {
        transition: 0.2s linear !important;
      }
    }
    &--icon {
      position: absolute;
      bottom: 2px;
      left: 2px;
      min-width: 20px;
      height: 20px;
      border-radius: 6px;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 3;
      .v-icon {
        color: $bh-white;
        font-size: 12px;
      }
      span {
        font-size: 0.6rem;
        padding-right: 2px;
        color: $bh-white;
        font-weight: bold;
        padding-top: 2px;
      }
    }
  }
  &__text {
    flex-grow: 1;
    flex-shrink: 1;
    padding: 0 8px;
    position: relative;
    max-width: 100%;
    h5 {
      color: $bh-black;
      max-height: 2.5em;
      overflow: hidden;
      margin: 0;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: calc(100% - 16px);
    }
    h6 {
      color: $bh-gray--2;
    }
    p {
      margin: 0;
      font-size: 0.8rem;
      max-width: 90%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &__more {
    flex-shrink: 0;
    width: 32px;
    opacity: 0;
    z-index: 1;
    position: relative;
    transition-duration: 300ms;
    position: absolute;
    top: 20px;
    right: 4px;
  }
  &__collection {
    position: absolute;
    right: $gutter/2;
    top: $gutter/2;
    transition-duration: 300ms;
    &.owned {
      background: $bh-blue;
      .v-icon {
        color: $bh-white;
      }
    }
  }
  &__quick-edit {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    padding-top: 4px;
    padding-left: 8px;
    &.series {
      flex-direction: column;
    }
    &.activity {
      margin-top: 8px;
    }
    &--item {
      display: flex;
      flex-wrap: wrap;
      h5 {
        width: 100%;
      }
    }
    &--input {
      width: 36px;
      text-align: center;
      margin-right: 4px;
      strong {
        display: block;
      }
      &:empty {
        display: none;
      }
      .v-input__slot {
        background: $bh-blue--4 !important;
        padding: 0 8px !important;
        font-size: 0.8rem;
      }
      .v-input__control {
        min-height: 26px !important;
      }
      span {
        font-size: 0.6rem;
      }
    }
  }
  &__report {
    width: 100%;
    display: flex;
    align-items: flex-start;
    .rating-stars {
      margin-top: 4px;
    }
    .activity-skipped {
      font-size: 10px;
      color: $bh-gray;
      padding-right: 10px;
    }
    &-rating-comment {
      position: relative;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }
  }
}
</style>
